import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Scale, Save, Ruler, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const AREE = [
  { value: 'tutte', label: 'Tutte le aree' },
  { value: 'gialla', label: 'Area Gialla' },
  { value: 'verde', label: 'Area Verde' },
];

const mesiOpzioni = () => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ value: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`, label: m.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }) });
  }
  return out;
};

export default function RilevazionePesiDialog({ open, onClose, onSaved }) {
  const [stato, setStato] = useState(null);
  const [mese, setMese] = useState(mesiOpzioni()[0].value);
  const [area, setArea] = useState('tutte');
  const [pesi, setPesi] = useState({});
  const [altezze, setAltezze] = useState({});
  const [soloMancanti, setSoloMancanti] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [segnalazioni, setSegnalazioni] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/analisi/rilevazione-pesi/stato', { params: { mese, area } })
      .then(r => {
        setStato(r.data);
        const p = {}, a = {};
        r.data.ospiti.forEach(o => { p[o.ospite_id] = o.peso_mese ?? ''; a[o.ospite_id] = o.altezza_cm ?? ''; });
        setPesi(p); setAltezze(a);
      })
      .catch(() => toast.error('Errore caricamento ospiti'))
      .finally(() => setLoading(false));
  }, [mese, area]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const visibiliPeso = useMemo(() => {
    if (!stato) return [];
    return stato.ospiti.filter(o => !soloMancanti || !o.rilevato);
  }, [stato, soloMancanti]);

  const visibiliAltezza = useMemo(() => {
    if (!stato) return [];
    return stato.ospiti.filter(o => !soloMancanti || !o.altezza_cm);
  }, [stato, soloMancanti]);

  const salvaPesi = async () => {
    const rilevazioni = Object.entries(pesi)
      .filter(([id, v]) => v !== '' && Number(v) > 20 && Number(v) !== (stato.ospiti.find(o => o.ospite_id === id)?.peso_mese ?? null))
      .map(([ospite_id, v]) => ({ ospite_id, peso: Number(v) }));
    if (!rilevazioni.length) { toast.error('Inserisci almeno un peso nuovo'); return; }
    setSaving(true);
    try {
      const res = await api.post('/analisi/rilevazione-pesi', { data_rilevazione: `${mese}-01`, rilevazioni });
      toast.success(`${res.data.registrati} pesi registrati${res.data.totale_segnalazioni ? `, ${res.data.totale_segnalazioni} segnalazioni al medico` : ''}`);
      setSegnalazioni(res.data.segnalazioni || []);
      load();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSaving(false);
  };

  const salvaAltezze = async () => {
    const lista = Object.entries(altezze)
      .filter(([id, v]) => v !== '' && Number(v) >= 80 && Number(v) !== (stato.ospiti.find(o => o.ospite_id === id)?.altezza_cm ?? null))
      .map(([ospite_id, v]) => ({ ospite_id, altezza_cm: Number(v) }));
    if (!lista.length) { toast.error('Inserisci almeno un\'altezza nuova'); return; }
    setSaving(true);
    try {
      const res = await api.put('/analisi/altezze', { altezze: lista });
      toast.success(`${res.data.aggiornati} altezze aggiornate: il BMI viene ora calcolato`);
      load();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="rilevazione-pesi-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scale className="w-4 h-4 text-brand" />Rilevazione Peso e Altezze</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={mese} onValueChange={setMese}>
            <SelectTrigger className="w-44 h-9 text-xs" data-testid="pesi-mese-select"><SelectValue /></SelectTrigger>
            <SelectContent>{mesiOpzioni().map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="pesi-area-select"><SelectValue /></SelectTrigger>
            <SelectContent>{AREE.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={soloMancanti} onChange={e => setSoloMancanti(e.target.checked)} data-testid="pesi-solo-mancanti" />
            Mostra solo i mancanti
          </label>
          {stato && (
            <div className="flex gap-1.5 ml-auto text-[10px]">
              <Badge variant="outline">{stato.rilevati}/{stato.totale} pesati nel mese</Badge>
              <Badge variant="outline" className={stato.senza_altezza ? 'text-amber-600 border-amber-200' : ''}>{stato.senza_altezza} senza altezza</Badge>
            </div>
          )}
        </div>

        {loading ? <p className="text-xs text-slate-400 py-8 text-center">Caricamento...</p> : !stato ? null : (
          <Tabs defaultValue="pesi">
            <TabsList>
              <TabsTrigger value="pesi" className="text-xs" data-testid="tab-pesi"><Scale className="w-3.5 h-3.5 mr-1" />Pesi del mese ({visibiliPeso.length})</TabsTrigger>
              <TabsTrigger value="altezze" className="text-xs" data-testid="tab-altezze"><Ruler className="w-3.5 h-3.5 mr-1" />Altezze ({visibiliAltezza.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="pesi" className="mt-3 space-y-3">
              {segnalazioni.length > 0 && (
                <div className="p-3 rounded-xl border border-red-200 bg-red-50 text-xs" data-testid="pesi-segnalazioni">
                  <p className="font-semibold text-red-800 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Segnalazioni generate per il medico</p>
                  <ul className="space-y-0.5 text-red-700">
                    {segnalazioni.map((s, i) => <li key={i}><b>{s.ospite_nome}</b> ({s.peso} kg{s.bmi ? `, BMI ${s.bmi}` : ''}): {s.rischio_motivi[0]}</li>)}
                  </ul>
                </div>
              )}
              {visibiliPeso.length === 0 ? (
                <p className="text-xs text-emerald-600 py-8 text-center flex items-center justify-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Tutti gli ospiti sono stati pesati in questo mese</p>
              ) : (
                <div className="max-h-[46vh] overflow-y-auto rounded-xl border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 sticky top-0">
                      <tr><th className="text-left px-2 py-1.5">Ospite</th><th className="px-2">Camera</th><th className="px-2">Peso prec.</th><th className="px-2 w-28">Peso (kg)</th><th className="px-2">Variazione</th></tr>
                    </thead>
                    <tbody>
                      {visibiliPeso.map(o => {
                        const nuovo = Number(pesi[o.ospite_id]);
                        const delta = o.peso_precedente && nuovo ? ((nuovo - o.peso_precedente) / o.peso_precedente * 100) : null;
                        return (
                          <tr key={o.ospite_id} className="border-t border-slate-100" data-testid={`peso-row-${o.ospite_id}`}>
                            <td className="px-2 py-1 font-medium text-slate-700">{o.ospite_nome}</td>
                            <td className="px-2 text-center text-slate-500">{o.stanza || '-'}</td>
                            <td className="px-2 text-center text-slate-500">{o.peso_precedente ? `${o.peso_precedente} kg` : '-'}<br /><span className="text-[9px] text-slate-400">{o.data_precedente || ''}</span></td>
                            <td className="px-2 py-1">
                              <Input type="number" step="0.1" value={pesi[o.ospite_id]}
                                onChange={e => setPesi(p => ({ ...p, [o.ospite_id]: e.target.value }))}
                                className="h-8 text-xs" placeholder="kg" data-testid={`peso-input-${o.ospite_id}`} />
                            </td>
                            <td className={`px-2 text-center font-semibold ${delta === null ? 'text-slate-400' : delta <= -5 ? 'text-red-600' : delta < 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                              {delta === null ? '-' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={salvaPesi} disabled={saving} className="bg-brand hover:bg-brand-dark text-white" data-testid="pesi-salva-btn">
                  <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Salvataggio...' : 'Registra pesi'}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="altezze" className="mt-3 space-y-3">
              <p className="text-[11px] text-slate-500">L'altezza serve a calcolare il BMI e a valutare il rischio di malnutrizione. Valori ammessi: 80-230 cm.</p>
              {visibiliAltezza.length === 0 ? (
                <p className="text-xs text-emerald-600 py-8 text-center flex items-center justify-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Tutti gli ospiti hanno l'altezza registrata</p>
              ) : (
                <div className="max-h-[46vh] overflow-y-auto rounded-xl border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 sticky top-0">
                      <tr><th className="text-left px-2 py-1.5">Ospite</th><th className="px-2">Camera</th><th className="px-2 w-32">Altezza (cm)</th><th className="px-2">Peso attuale</th></tr>
                    </thead>
                    <tbody>
                      {visibiliAltezza.map(o => (
                        <tr key={o.ospite_id} className="border-t border-slate-100" data-testid={`altezza-row-${o.ospite_id}`}>
                          <td className="px-2 py-1 font-medium text-slate-700">{o.ospite_nome}</td>
                          <td className="px-2 text-center text-slate-500">{o.stanza || '-'}</td>
                          <td className="px-2 py-1">
                            <Input type="number" value={altezze[o.ospite_id]}
                              onChange={e => setAltezze(a => ({ ...a, [o.ospite_id]: e.target.value }))}
                              className="h-8 text-xs" placeholder="cm" data-testid={`altezza-input-${o.ospite_id}`} />
                          </td>
                          <td className="px-2 text-center text-slate-500">{o.peso_mese ?? o.peso_precedente ?? '-'} {(o.peso_mese ?? o.peso_precedente) ? 'kg' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={salvaAltezze} disabled={saving} className="bg-brand hover:bg-brand-dark text-white" data-testid="altezze-salva-btn">
                  <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Salvataggio...' : 'Salva altezze'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}

        <div className="flex justify-end pt-1">
          <Label className="text-[10px] text-slate-400">I pesi registrati alimentano automaticamente gli alert nutrizionali e il grafico del trend</Label>
        </div>
      </DialogContent>
    </Dialog>
  );
}
